// Import Core
import React, { useState } from "react";
import { Link } from "gatsby";
import styled from "styled-components";
// Import Libraries
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
// Import Swiper Styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
// Import Components
import Button from "../ui/buttons/Button";
import Stars from "../ui/Stars";
import IconList from "../ui/IconList";
import TextButton from "../ui/buttons/TextButton";
import RelatedTripsLocations from "./RelatedTripsLocations";
// Import Utils
import { usePlaceholderImage } from "../../utils/useRandomPlaceholderImage";
import { getSlug } from "../../utils/getSlug";
import { renderReactIcon } from "../../utils/renderReactIcons";
import { getShortDate } from "../../utils/datetime/getShortDate";
import { getAuthor } from "../../utils/getAuthor";
import { toSlug } from "../../utils/search/toSlug";
import { getCardTagline } from "../../utils/getCardTagline";

const CardCarouselProductStyling = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* padding: var(--global-padding); */
  padding: 80px 0 80px 40px;
  background: var(--white);
  color: var(--neutral-75);
  gap: 64px;
  @media (max-width: 992px) {
    /* padding: 64px 40px; */
    gap: 40px;
  }
  @media (max-width: 768px) {
    padding: var(--global-padding-mobile);
  }

  .section-title {
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 0 64px 0 24px;
    @media (max-width: 768px) {
      padding: 0 20px 0 0;
      flex-direction: column;
      gap: 24px;
    }
    .content {
      display: flex;
      max-width: 768px;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      @media (max-width: 768px) {
        gap: 20px;
      }
      .title {
        color: var(--black);
      }
    }
  }

  .swiper {
    position: relative;
    width: 100%;
    height: fit-content;
    display: flex;
    flex-direction: column;
    z-index: 0;
    padding: 0 0 0 24px;
    padding-bottom: calc(48px + 28px);
    @media (max-width: 768px) {
      padding: 0;
      padding-bottom: calc(32px + 28px);
    }
    /* Hide extra pagination padding if all slides fit screen - no pagination will show  */
    &:has(.swiper-button-next.swiper-button-lock) {
      padding-bottom: 0;
    }
    &:after {
      content: "";
      background: linear-gradient(
        270deg,
        var(--white) 0%,
        rgba(255, 255, 255, 0) 100%
      );
      position: absolute;
      right: 0;
      width: 254px;
      height: 100%;
      z-index: 1;
      transition: var(--basic-transition);
      opacity: 1;
      @media (max-width: 768px) {
        width: 54px;
      }
    }
    &:hover {
      &:after {
        opacity: 0;
        z-index: 0;
      }
    }
    .swiper-wrapper {
      /* Add padding so box-shadow isn't cut off */
      padding: 4px;
      /* gap: 36px; */
      .swiper-slide {
        height: auto;
        width: 380px;
        max-width: 25vw;
        @media (max-width: 1200px) {
          max-width: 380px;
        }
        @media (max-width: 768px) {
          max-width: 100%;
          width: 100%;
        }
        &:last-child {
          margin-right: 0;
          border-right: none;
        }
        .card {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          width: inherit;
          background-color: var(--white);
          box-shadow: var(--box-shadow-small);

          .product-type-wrapper {
            display: flex;
            position: absolute;
            top: 16px;
            left: 12px;
            align-items: flex-start;
            gap: 16px;
          }

          .product-type {
            padding: 4px 8px;
            border-radius: 4px;
            background: var(--red-light);
            color: var(--red);
            z-index: 1;
          }

          .highly-recommended{
            padding: 4px 8px;
            border-radius: 4px;
            background: var(--blue-10);
            color: var(--blue);
            z-index: 1;
          }

          .price {
            color: var(--blue);
            font-weight: 700;
            font-size: 16px;
            line-height: 24px;
          }
          .gatsby-image-wrapper-constrained {
            width: 100%;
          }
          img {
            display: block;
            width: 100%;
            height: 280px;
            object-fit: cover;
          }
          .content {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            padding: 24px;
            gap: 8px;
            width: 100%;
            .title {
              color: var(--black);
            }
            .location {
              color: var(--neutral-75);
            }
            .ratings {
              display: flex;
              justify-content: center;
              align-items: center;
              gap: 16px;
              .star-rating {
                display: flex;
                .star {
                  width: 18.5px;
                  height: 17.6px;
                }
              }
              .hotel-type {
                text-transform: capitalize;
              }
            }
            .short-excerpt {
              color: var(--neutral-75);
            }
            .read-more-btn {
              padding-top: 16px;
            }
            .special-offer {
              .offer {
                color: var(--blue);
                .badge {
                  padding: 4px 8px;
                  margin: 0 8px 0 -5px;
                  border-radius: 4px;
                  background: var(--blue-75);
                  color: var(--white);
                }
              }
            }
            .included-items {
              display: flex;
              gap: 16px;
              align-items: flex-end;
              position: relative;
              .popover {
                .popover-wrapper {
                  .popover.popover-top {
                    /* Height of popover trigger * -2.5 */
                    top: calc(24px * -2.5);
                  }
                }
              }
              .popover-children {
                .includes-popover {
                  display: flex;
                  height: 24px;
                  padding: 8px;
                  justify-content: center;
                  align-items: center;
                  gap: 8px;
                  border-radius: 4px;
                  background-color: var(--red-10);
                  color: var(--red);
                  position: relative;
                  cursor: pointer;
                }
              }
            }
            .review-info {
              display: flex;
              align-items: center;
              gap: 8px;
              flex-flow: wrap;
              .published-date {
                color: var(--neutral-75);
              }
              .author {
                color: var(--neutral-75);
                span {
                  color: var(--red);
                }
              }
            }
            .related {
              margin-top: 16px;
            }
          }

          &.orange-card {
            .product-type {
              background: var(--orange-10);
              color: var(--orange-120);
            }
            .content {
              .tour-info {
                color: var(--orange-120);
                .theme-tags {
                  /* display: flex;
									padding-right: 8px;
									align-items: flex-start;
									.tag {
										&:not(:first-child) {
											&:before {
												content: ', ';
											}
										}
									} */
                }
                .departure-dates {
                  .month {
                    background-color: var(--orange-10);
                    color: var(--orange-120);
                  }
                }
              }
              .included-items {
                .icon-list-item {
                  .list-icon {
                    background-color: var(--orange-10);
                    svg {
                      color: var(--orange-110);
                    }
                  }
                }
                .popover-children {
                  .includes-popover {
                    background-color: var(--orange-10);
                    color: var(--orange);
                  }
                }
              }
            }
          }
        }
      }
    }
    .swiper-button-prev,
    .swiper-button-next {
      background-color: var(--red);
      width: 48px;
      padding: 9px 0px;
      &:after {
        color: var(--white);
        font-size: 20px;
        font-style: normal;
        font-weight: 900;
        line-height: 150%;
      }
      @media (max-width: 768px) {
        top: unset;
        bottom: 0;
        z-index: 2;
      }
      &:hover {
        background-color: var(--red-110);
      }
      &.swiper-button-disabled {
        display: none;
      }
    }
    .swiper-button-prev,
    .swiper-rtl .swiper-button-next {
      left: 0;
    }
    .swiper-button-next,
    .swiper-rtl .swiper-button-prev {
      right: 40px;
    }
    .swiper-pagination {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      margin: 0;
      z-index: 1;
      .swiper-pagination-bullet {
        width: 8px;
        height: 8px;
        border-radius: 0;
        opacity: 1;
        background-color: var(--neutral-50);
        &.swiper-pagination-bullet-active {
          background-color: var(--grey);
        }
      }
    }
  }
  &.orange-bg {
    background-color: var(--orange-10);
    .swiper-wrapper {
      .swiper-slide {
        .card {
          .product-type {
            background: var(--orange-10);
            color: var(--orange-120);
          }
          .content {
            .tour-info {
              color: var(--orange-120);
              display: flex;
              align-items: flex-start;
              align-content: flex-start;
              gap: 8px;
              align-self: stretch;
              flex-wrap: wrap;
              .theme-tags {
                display: flex;
                padding-right: 8px;
                align-items: flex-start;
                .tag {
                  &:not(:first-child) {
                    &:before {
                      content: ", ";
                    }
                  }
                }
              }
              .departure-dates {
                display: flex;
                justify-content: flex-end;
                align-items: flex-start;
                align-content: flex-start;
                gap: 8px;
                flex: 1 0 0;
                flex-wrap: wrap;
                .month {
                  display: flex;
                  padding: 4px 8px;
                  align-items: flex-start;
                  border-radius: 4px;
                  background-color: var(--orange-10);
                  color: var(--orange-120);
                  font-size: 14px;
                  text-transform: uppercase;
                }
              }
            }
            .included-items {
              .icon-list-item {
                .list-icon {
                  background-color: var(--orange-10);
                  svg {
                    color: var(--orange-110);
                  }
                }
              }
              .popover-children {
                .includes-popover {
                  background-color: var(--orange-10);
                  color: var(--orange);
                }
              }
            }
          }
        }
      }
    }
  }
  &.red-bg {
    background-color: var(--red-10);
  }
`;

const CardContent = ({
  productType,
  productTagline,
  title,
  location,
  image,
  starRating,
  hotelType,
  themeTags,
  // departureDates,
  darwinDepartureDates,
  reviewPublishedDate,
  reviewAuthor,
  relatedTrip,
  relatedLocations,
  content,
  specialOffer,
  includes,
  popoverIcons,
  slug,
  priceFrom,
  highlyRecommended
}) => {
  const { randomPlaceholderImage } = usePlaceholderImage();

  //first available CulturalTour
  let firstCulturalTour;

  if (productType === "culturalTour") {
    firstCulturalTour = darwinDepartureDates.filter(
      (trip) =>
        new Date(trip.landonlydeparturedate).getTime() > Date.now() &&
        trip.departurestatus === "On Sale"
    );
  }

  return (
    <div
      className={`card${productType === "culturalTour" ? " orange-card" : ""}`}
    >
      <div className="product-type-wrapper">
        <p className="product-type small bold">{productTagline}</p>
        {
          (productType === "hotelBreak") && highlyRecommended ? <p className="highly-recommended small bold">Highly Recommended</p> : ''
        }
      </div>
      {/* Devs decided against images carousel */}
      {image && image.asset ? (
        <Link to={slug} className="">
          <GatsbyImage image={getImage(image.asset)} alt={title} />
        </Link>
      ) : (
        <Link to={slug} className="">
          <img
            src={randomPlaceholderImage}
            alt={title}
            className="placeholder-img"
          />
        </Link>
      )}

      <div className="content">
        <Link to={slug} className="">
          <h4 className="alt title">{title}</h4>
        </Link>
        {location && <p className="location bold">{location}</p>}
        {productType === "review" && (
          <div className="review-info">
            {reviewPublishedDate && (
              <p className="published-date medium">
                {getShortDate(reviewPublishedDate)}
              </p>
            )}

            {reviewAuthor && reviewAuthor.link && (
              <p className="author">
                by{" "}
                <span>
                  <Link to={reviewAuthor.link}>{reviewAuthor.name}</Link>
                </span>
              </p>
            )}
          </div>
        )}
        {/* HotelBreak: */}
        {productType === "hotelBreak" && (starRating || hotelType) && (
          <div className="ratings">
            {starRating && (
              <div className="star-rating">
                <Stars starRating={starRating} />
              </div>
            )}
            {hotelType && hotelType !== "none" && (
              <p className="hotel-type bold italic red">{hotelType}</p>
            )}
          </div>
        )}

        {!specialOffer && <p className="short-excerpt">{content}</p>}
        {specialOffer && (
          <div className="special-offer">
            <p className="offer">
              <span className="badge small bold">Special Offer</span>
              {specialOffer}
            </p>
          </div>
        )}

        {/* {productType === 'culturalTour' && (themeTags || departureDates) && ( */}
        {productType === "culturalTour" && themeTags && (
          <div className="tour-info">
            {themeTags &&
              themeTags.themeTags &&
              themeTags.themeTags.tag &&
              themeTags.themeTags.tag.length > 0 && (
                <p className="theme-tags">
                  {themeTags.themeTags.tag.map((tag, index) => (
                    <span className="tag bold italic" key={index}>
                      {tag.label}
                    </span>
                  ))}
                </p>
              )}
            {/* // TODO:  Add Popover of dates? */}
            {/* FOLLOW UP: will be darwin? */}
            {/* {departureDates && departureDates.length > 0 && (
							<div className="departure-dates">
								{departureDates.map((date, index) => (
									<p className="month" key={index}>
										{date.startMonth}
									</p>
								))}
							</div>
						)} */}
          </div>
        )}
        {priceFrom && productType === "tailorMadeHoliday" && (
          <p className="price">{priceFrom}</p>
        )}

        {/* Cultural price */}
        {!!firstCulturalTour?.[0] && productType === "culturalTour" && (
          <p className="price">
            {firstCulturalTour[0].landonlyduration} nights price from £
            {firstCulturalTour[0].landonlyadultprice -
              firstCulturalTour[0].singlesupplement}{" "}
            per person
          </p>
        )}

        {/* <p>TODO: Darwin INFO HERE!!!</p> */}
        {includes && includes.length > 0 && (
          <div className="included-items">
            <IconList
              iconListItems={includes}
              iconLayout="minimal"
              addPopover={true}
              popoverListItems={popoverIcons}
              id={slug}
              addText={true}
            />
          </div>
        )}
        <Link to={slug} id="read-more-product" className="read-more-btn">
          <TextButton
            size="md"
            className=""
            value={"Read More"}
            clickHandler={false}
            isDisabled={false}
          />
        </Link>

        {productType === "review" && (
          <RelatedTripsLocations
            relatedTrip={relatedTrip}
            relatedLocations={relatedLocations}
          />
        )}
      </div>
    </div>
  );
};

const CardCarouselProduct = ({
  productType,
  addToAnchor,
  subtitle,
  title,
  content,
  linkText,
  link,
  cardList,
}) => {
  // FIX:  THESE WILL ALL NEED TO BE GETSLUG()???
let slugPath = null;
const isExternal = link?.displayExternalLink;
if (link || link.link) {
  if (typeof link === "string") {
    slugPath = link;
  } else if (isExternal) {
    slugPath = link.link;
  } else if(link.link?.slug) {
    slugPath = getSlug(link.link)
  }
}
  const className = `card-carousel ${addToAnchor && "anchor-link"} ${
    productType === "culturalTour"
      ? " orange-bg"
      : productType === "tailorMadeHoliday"
      ? " red-bg"
      : ""
  }`;

  return (
    <CardCarouselProductStyling
      id={addToAnchor ? `anchor_${toSlug(title)}` : ""}
      className={className}
    >
      <div className="section-title">
        <div className="content">
          {subtitle && <p className="tagline">{subtitle}</p>}
          <h2 className="title">{title}</h2>
          <p>{content}</p>
        </div>
        { slugPath && (
          isExternal ? (
            // Render external link with an anchor tag
            <a href={slugPath} target="_blank" rel="noopener noreferrer">
              <Button
                size="md"
                btnStyle="outline"
                className=""
                value={linkText}
                clickHandler={false}
                isDisabled={false}
              />
            </a>
          ) : (
            // Render internal link with React Router's Link component
            <Link to={slugPath}>
              <Button
                size="md"
                btnStyle="outline"
                className=""
                value={linkText}
                clickHandler={false}
                isDisabled={false}
              />
            </Link>
          )
          )}
      </div>

      <Swiper
        slidesPerView={"auto"}
        spaceBetween={36}
        pagination={{
          clickable: true,
        }}
        navigation={true}
        modules={[Pagination, Navigation]}
        className="card-carousel-swiper"
      >
        {cardList &&
          cardList.length > 0 &&
          cardList.map((card, index) => {
            // Some cards have one location, some have an array so we either set 'location' or 'locations'
            const location =
              card.city && card.country
                ? `${card.city.name}, ${card.country.name}`
                : card.city
                  ? card.city.name
                  : card.country
                    ? card.country.name
                    : null;

            let whatsIncluded = [];
            // Check if the corresponding country has an includes section, if not then check if it has a city includes section otherwise show product includes section
            if (
              card.country &&
              card.country.countryIncludes &&
              card.country.countryIncludes.length > 0
            ) {
              whatsIncluded = card.country.countryIncludes;
            } else if (
              card.city &&
              card.city.cityIncludes &&
              card.city.cityIncludes.length > 0
            ) {
              whatsIncluded = card.city.cityIncludes;
            } else if (card.includes && card.includes.length > 0) {
              whatsIncluded = card.includes;
            }

            const locations =
              card.includedCities && card.includedCities.length > 0
                ? card.includedCities.map((item) => item.name).join(", ")
                : null;

            // TODO: THIS CAN BE REUSABLE COMPONENT?
            const includesIconList =
              whatsIncluded && whatsIncluded.length > 0
                ? whatsIncluded.map((item) => {
                    const Icon = item.icon
                      ? renderReactIcon(item.icon.name)
                      : null;
                    return {
                      icon: Icon,
                      title: item.name,
                    };
                  })
                : null;

            const iconsToShow = includesIconList
              ? includesIconList.slice(0, 4)
              : null;
            const popoverIcons =
              includesIconList && includesIconList.length > 4
                ? includesIconList.slice(4)
                : [];

            // Review Variables
            // const reviewAuthor = card.author ? getAuthor(card) : null;

            return (
              <SwiperSlide key={index}>
                <CardContent
                  productType={card._type}
                  // productTagline={productTagline}
                  productTagline={getCardTagline(card._type)}
                  title={card.title}
                  location={location ? location : locations}
                  image={card.thumbnail}
                  images={card.images}
                  // ShortBreak
                  starRating={card.starRating}
                  hotelType={card.hotelType}
                  // CulturalTour
                  themeTags={card.themeTags}
                  darwinDepartureDates={card.darwinDepartureDates}
                  // departureDates={card.departureDates}
                  // Reviews
                  reviewPublishedDate={card.publishedDate}
                  // reviewAuthor={reviewAuthor}
                  reviewAuthor={card.author}
                  relatedTrip={card.relatedTrip}
                  relatedLocations={card.relatedLocations}
                  content={card.excerpt}
                  specialOffer={
                    card.relatedSpecialOffers &&
                    card.relatedSpecialOffers.length > 0
                      ? card.relatedSpecialOffers[0].excerpt
                      : card.specialOffer
                      ? card.specialOffer
                      : null
                  }
                  includes={iconsToShow}
                  popoverIcons={popoverIcons}
                  slug={getSlug(card)}
                  priceFrom={card.priceFrom}
                  highlyRecommended={card.highlyRecommended}
                />
              </SwiperSlide>
            );
          })}
      </Swiper>
    </CardCarouselProductStyling>
  );
};

export default CardCarouselProduct;
